import {
  Banner,
  Button,
  DataTable,
  DefinitionList,
  DetailLayout,
  ErrorState,
  FilterToolbar,
  notify,
  PageHeader,
  RichText,
  SearchInput,
  Section,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  TextAreaField,
  TextField,
  Toggle,
  useClientRows,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
  type SortValue,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";

export type Article = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  body: string;
  categoryId: string | null;
  category: string | null;
  status: "draft" | "published";
  visibility: "internal" | "public";
  helpfulYes: number;
  helpfulNo: number;
  views: number;
  publishedAt: string | null;
  updatedAt: string;
};
type Category = { id: string; name: string; position: number; articles: number };
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const useCategories = () => useQuery({ queryKey: ["console", "kb", "categories"], queryFn: () => api<{ categories: Category[] }>("/v1/console/kb/categories") });
const audience = (a: Pick<Article, "visibility">) => (a.visibility === "public" ? "Customers" : "Staff only");
const votes = (a: Article) => a.helpfulYes + a.helpfulNo;
const helpfulPct = (a: Article) => (votes(a) ? Math.round((a.helpfulYes / votes(a)) * 100) : null);

/** URL filter defaults. `category` is sent to the API as categoryId, and only when set (unchanged contract). */
const FILTERS = { q: "", status: "any", visibility: "any", category: "" };
const SORT: Record<string, SortValue<Article>> = {
  title: (a) => a.title,
  helpful: (a) => helpfulPct(a),
  views: (a) => a.views,
  updated: (a) => a.updatedAt,
};

/** Service > Knowledge base: articles for staff (internal) and customers (public help center). */
export function KnowledgePage() {
  const me = useConsoleMe();
  const navigate = useNavigate();
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const cats = useCategories();
  const query = new URLSearchParams({ q: v.q, status: v.status, visibility: v.visibility, ...(v.category ? { categoryId: v.category } : {}) });
  const list = useQuery({ queryKey: ["console", "kb", "articles", query.toString()], queryFn: () => api<{ articles: Article[] }>(`/v1/console/kb/articles?${query}`), placeholderData: keepPreviousData });
  const table = useClientRows(list.data?.articles ?? [], { values: SORT, initial: { key: "updated", dir: "desc" } });
  const items: FilterItem[] = [
    { key: "status", priority: 1, active: v.status !== "any", inline: <Select variant="toolbar" label="Status" value={v.status} active={v.status !== "any"} onChange={(status) => f.set({ status })} options={[{ value: "any", label: "Any" }, { value: "published", label: "Published" }, { value: "draft", label: "Draft" }]} /> },
    { key: "visibility", priority: 2, active: v.visibility !== "any", inline: <Select variant="toolbar" label="Audience" value={v.visibility} active={v.visibility !== "any"} onChange={(visibility) => f.set({ visibility })} options={[{ value: "any", label: "Any" }, { value: "public", label: "Customers" }, { value: "internal", label: "Staff only" }]} /> },
    { key: "category", priority: 3, active: v.category !== "", inline: <Select variant="toolbar" label="Category" value={v.category} active={v.category !== ""} onChange={(category) => f.set({ category })} options={[{ value: "", label: "Any" }, ...(cats.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))]} /> },
  ];
  const columns: Column<Article>[] = [
    {
      key: "title",
      header: "Article",
      primary: true,
      sortable: true,
      truncate: true,
      text: (a) => a.title,
      render: (a) => <Link className="ffa-rowlink" to={`/knowledge/${a.id}`}>{a.title}</Link>,
      sub: (a) => a.summary,
    },
    { key: "audience", header: "Audience", width: "8rem", render: (a) => audience(a) },
    { key: "status", header: "Status", width: "8rem", render: (a) => <StatusBadge domain="article" value={a.status} size="sm" /> },
    { key: "category", header: "Category", width: "9rem", mobile: "hide", truncate: true, text: (a) => a.category ?? "", render: (a) => a.category ?? <span className="ffa-muted">None</span> },
    { key: "helpful", header: "Helpful", numeric: true, sortable: true, width: "8.5rem", mobile: "hide", render: (a) => (votes(a) ? `${helpfulPct(a)}% of ${votes(a)}` : "-") },
    { key: "views", header: "Views", numeric: true, sortable: true, width: "6rem", mobile: "hide", render: (a) => a.views.toLocaleString("en-US") },
    { key: "updated", header: "Updated", sortable: true, sortFirst: "desc", width: "8.5rem", render: (a) => <time dateTime={a.updatedAt}>{when.format(new Date(a.updatedAt))}</time> },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Knowledge base"
        subtitle="Answers for the team and for customers. Published customer articles appear in the portal's help center."
        primaryAction={me.capabilities.includes("kb:write") ? <Link className="ffx-btn ffx-btn--primary" to="/knowledge/new">New article</Link> : undefined}
      />
      <FilterToolbar
        label="Article filters"
        search={<SearchInput label="Search articles" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
        meta={list.data ? `${list.data.articles.length.toLocaleString("en-US")} article${list.data.articles.length === 1 ? "" : "s"}` : null}
      />
      <DataTable
        caption="Articles"
        columns={columns}
        {...table}
        rowKey={(a) => a.id}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load articles", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={f.anyActive ? "No articles match." : "No articles yet. Write the first one."}
        onRowClick={(a) => navigate(`/knowledge/${a.id}`)}
        rowHref={(a) => `/knowledge/${a.id}`}
      />
    </section>
  );
}

const back = (
  <Link to="/knowledge" className="ffa-back">
    ← Knowledge base
  </Link>
);

/** Service > Knowledge base > article editor (DetailLayout): the fields, with a live preview of exactly what readers will see beside them. */
export function ArticleEditor() {
  const { articleId = "new" } = useParams();
  const isNew = articleId === "new";
  const me = useConsoleMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const cats = useCategories();
  const q = useQuery({ queryKey: ["console", "kb", "article", articleId], queryFn: () => api<Article>(`/v1/console/kb/articles/${articleId}`), enabled: !isNew });
  const [f, setF] = useState({ title: "", summary: "", body: "", categoryId: "", visibility: "internal" as Article["visibility"] });
  const [newCat, setNewCat] = useState("");
  useEffect(() => {
    if (q.data) setF({ title: q.data.title, summary: q.data.summary ?? "", body: q.data.body, categoryId: q.data.categoryId ?? "", visibility: q.data.visibility });
  }, [q.data]);
  const canWrite = me.capabilities.includes("kb:write");
  const done = () => void qc.invalidateQueries({ queryKey: ["console", "kb"] });
  const save = useMutation({
    mutationFn: (status?: "draft" | "published") => {
      const body = { title: f.title.trim(), summary: f.summary.trim() || null, body: f.body.trim(), categoryId: f.categoryId || null, visibility: f.visibility };
      return isNew ? api<Article>("/v1/console/kb/articles", { method: "POST", body }) : api<Article>(`/v1/console/kb/articles/${articleId}`, { method: "PATCH", body: { ...body, ...(status ? { status } : {}) } });
    },
    onSuccess: (a, status) => {
      done();
      notify.success(status === "published" ? "Published" : status === "draft" ? "Moved back to draft" : "Saved", status === "published" && a.visibility === "public" ? "Customers can find it in the help center." : undefined);
      if (isNew) navigate(`/knowledge/${a.id}`, { replace: true });
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const addCat = useMutation({
    mutationFn: () => api<Category>("/v1/console/kb/categories", { method: "POST", body: { name: newCat.trim() } }),
    onSuccess: (c) => {
      setNewCat("");
      setF((x) => ({ ...x, categoryId: c.id }));
      void qc.invalidateQueries({ queryKey: ["console", "kb", "categories"] });
    },
    onError: (e) => notify.error("Couldn't add the category", message(e)),
  });
  const valid = f.title.trim().length >= 3 && f.body.trim() !== "";
  const a = q.data;

  if (!isNew && q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout header={<PageHeader back={back} eyebrow="Article" title={<Skeleton width="18rem" height="2.25rem" />} subtitle={<Skeleton width="14rem" />} />} aside={<Section title="Preview"><SkeletonText lines={6} /></Section>} asideLabel="Preview">
          <Section><SkeletonText lines={8} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading article…</span>
      </section>
    );
  }
  if (!isNew && q.isError) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "Article not found" : "Article"} />
        {missing ? <Banner tone="warning" title="This article doesn't exist.">It may have been deleted, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this article" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }

  const actions = canWrite ? (
    !isNew ? (
      a?.status === "published" ? (
        { secondary: <Button onClick={() => save.mutate("draft")}>Unpublish</Button>, primary: <Button variant="primary" disabled={!valid} loading={save.isPending && save.variables === undefined} onClick={() => save.mutate(undefined)}>Save</Button> }
      ) : (
        { secondary: <Button disabled={!valid} loading={save.isPending && save.variables === undefined} onClick={() => save.mutate(undefined)}>Save</Button>, primary: <Button variant="primary" disabled={!valid} onClick={() => save.mutate("published")}>Publish</Button> }
      )
    ) : (
      { secondary: null, primary: <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate(undefined)}>Save draft</Button> }
    )
  ) : null;

  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow="Article"
            title={isNew ? "New article" : (a?.title ?? "Article")}
            badges={a ? <StatusBadge domain="article" value={a.status} /> : null}
            subtitle={a ? `${a.status === "published" ? `Published ${a.publishedAt ? when.format(new Date(a.publishedAt)) : ""}` : "Draft"} · ${a.visibility === "public" ? "Customers can read it once published" : "Staff only"} · ${a.views} views` : "Staff only until you publish it."}
            secondaryActions={actions?.secondary}
            primaryAction={actions?.primary}
          />
        }
        aside={
          <Section title="Preview" description="Exactly what readers will see." className="ffa-preview">
            <p className="ffa-preview__title">{f.title || "Untitled"}</p>
            {f.summary ? <p className="ffa-muted">{f.summary}</p> : null}
            {f.body.trim() ? <RichText text={f.body} /> : <p className="ffa-muted">Start writing to see the article.</p>}
          </Section>
        }
        asideLabel="Article preview"
      >
        <Section fieldset disabled={!canWrite} title="Article">
          <div className="ffx-fields">
            <TextField className="ffx-span" label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={160} />
            <TextField className="ffx-span" label="Summary" optional value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} maxLength={300} hint="One line shown in search results" />
            <Select label="Category" value={f.categoryId} onChange={(v) => setF({ ...f, categoryId: v })} options={[{ value: "", label: "None" }, ...(cats.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))]} />
            {canWrite ? (
              <div className="ffa-inlineform">
                <TextField label="New category" optional value={newCat} onChange={(e) => setNewCat(e.target.value)} maxLength={60} />
                <Button size="lg" disabled={newCat.trim().length < 2} loading={addCat.isPending} onClick={() => addCat.mutate()}>Add</Button>
              </div>
            ) : null}
            <Toggle className="ffx-span" look="switch" label="Customers can read this (help center)" checked={f.visibility === "public"} onChange={(on) => setF({ ...f, visibility: on ? "public" : "internal" })} />
            <TextAreaField className="ffx-span" label="Article" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={16} maxLength={50000} hint="## for a heading, - for a bullet, 1. for steps, **bold**. A blank line starts a new paragraph." />
          </div>
        </Section>
        {a ? (
          <Section title="Readers">
            <DefinitionList
              items={[
                { term: "Audience", detail: audience(a) },
                { term: "Views", detail: <span className="ffx-num">{a.views.toLocaleString("en-US")}</span> },
                { term: "Helpful", detail: votes(a) ? `${helpfulPct(a)}% of ${votes(a)} votes` : "No votes yet" },
                { term: "Updated", detail: when.format(new Date(a.updatedAt)) },
              ]}
            />
          </Section>
        ) : null}
      </DetailLayout>
    </section>
  );
}
